<template>
  <Callout.Root v-for="view in views" :key="view.id" :variant="view.severity">
    <template v-if="view.title" #title>{{ view.title }}</template>
    {{ view.body }}
    <template v-if="showAction(view.action)" #actions>
      <Callout.Action
        :to="view.action?.to"
        :href="view.action?.href"
        @click="view.action?.onClick"
      >
        {{ view.action?.label }}
      </Callout.Action>
    </template>
  </Callout.Root>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

import type { components } from "@/autogen/internal-openapi";
import URLS from "@/autogen/urls";
import Callout from "@/design_system/Callout";
import { useBlockersQuery } from "@/lib/api/queries/home";
import { useXsrfHeaders } from "@/lib/api/queries/utils";
import { showToast } from "@/lib/toasts";
import type { RouteLocation } from "@/router/types";

type BlockerId = components["schemas"]["Blocker"]["id"];

type BlockerAction = {
  label: string;
  to?: RouteLocation;
  href?: string;
  onClick?: () => void;
  hideOnRoute?: string;
};

type BlockerView = {
  title?: string;
  body: string;
  action?: BlockerAction;
};

type BlockerContext = {
  metadata: Record<string, unknown>;
  resend: () => void;
};

type BlockerResolver = BlockerView | ((ctx: BlockerContext) => BlockerView);

const route = useRoute();
const query = useBlockersQuery();
const headers = useXsrfHeaders();

const resend = () => {
  // We deliberately don't await this; we just want to show a toast immediately.
  fetch(URLS["resend-confirmation-email"](), {
    method: "POST",
    headers: {
      ...headers.value,
      accept: "application/json",
      "content-type": "application/json",
    },
  });
  showToast({
    type: "inform",
    title: `Confirmation email sent`,
    message: `Check your inbox to confirm your account.`,
  });
};

const formatDeletionDate = (raw: unknown): string => {
  if (typeof raw !== "string") return "";
  return new Date(raw).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const PAUSED_BODY =
  "Your account has been paused for a human review. Please provide some information so we can verify your account.";

const PROVIDE_INFORMATION_ACTION: BlockerAction = {
  label: "Provide information",
  to: { name: "/home" },
  hideOnRoute: "/home",
};

const BLOCKER_VIEWS: Record<BlockerId, BlockerResolver> = {
  billing_past_due: {
    title: "Account past due.",
    body: "Update your payment info to resume sending.",
    action: { label: "Update payment info", href: "/settings/billing" },
  },
  confirmation_emails_blocked: {
    title: "Opt-in confirmations not sending.",
    body: "Your account is over its plan's subscriber limit, so confirmation emails to new subscribers aren't being delivered. Upgrade to resume.",
    action: {
      label: "Upgrade plan",
      to: { name: "/settings/billing/pricing" },
    },
  },
  draft_send_blocked: {
    body: "Sending drafts has been disabled. Reach out and we'll help sort it out.",
    action: {
      label: "Contact support",
      href: "mailto:support@buttondown.email",
    },
  },
  email_address_mismatch: {
    body: "Your email address doesn't match sending domain — emails will fail to send.",
  },
  newsletter_deletion_scheduled: ({ metadata }) => ({
    body: `This newsletter is scheduled for deletion on ${formatDeletionDate(
      metadata.deletion_date
    )}.`,
    action: {
      label: "Cancel deletion",
      to: { name: "/settings/danger-zone" },
    },
  }),
  newsletter_disabled: ({ metadata }) =>
    metadata.kyc_submitted
      ? {
          body: "Thanks for the info — a real person is reviewing your account. This usually takes a few hours.",
          action: {
            label: "Learn more",
            href: "https://docs.buttondown.com/account-review",
          },
        }
      : { body: PAUSED_BODY, action: PROVIDE_INFORMATION_ACTION },
  newsletter_dormant: {
    body: "This newsletter is marked dormant.",
    action: {
      label: "Re-enable newsletter",
      to: { name: "/settings/danger-zone" },
    },
  },
  newsletter_permanently_disabled: ({ metadata }) =>
    metadata.kyc_submitted
      ? {
          body: "We're unable to approve your account. Please contact us if you have any questions.",
          action: {
            label: "Contact us",
            href: "mailto:support@buttondown.com",
          },
        }
      : { body: PAUSED_BODY, action: PROVIDE_INFORMATION_ACTION },
  newsletter_unconfirmed: ({ resend: resendFn }) => ({
    body: "Confirm your email address so subscribers can confirm their subscription.",
    action: { label: "Resend", onClick: resendFn },
  }),
  newsletter_undeliverable: {
    body: "Can't reach your email address — contact support@buttondown.com to resolve this issue.",
  },
  paid_subscriptions_disabled: {
    body: "Stripe has disabled paid subscriptions for this newsletter.",
    action: {
      label: "Go to Stripe",
      href: "https://dashboard.stripe.com/account/status",
    },
  },
  paid_subscriptions_disconnected: {
    body: "Stripe account disconnected.",
    action: {
      label: "Reconnect Stripe",
      to: { name: "/settings/paid-subscriptions" },
    },
  },
  sending_domain_invalid: {
    body: "Finish setting up your sending domain to send emails.",
    action: { label: "Learn more", to: { name: "/settings/domains.sending" } },
  },
  test_mode_enabled: {
    body: "Test mode enabled — all emails go to you.",
    action: {
      label: "Disable test mode",
      to: { name: "/settings/danger-zone" },
      hideOnRoute: "/settings/danger-zone",
    },
  },
};

const resolveView = (
  id: BlockerId,
  metadata: Record<string, unknown>
): BlockerView => {
  const resolver = BLOCKER_VIEWS[id];
  return typeof resolver === "function"
    ? resolver({ metadata, resend })
    : resolver;
};

const views = computed(() =>
  (query.data.value ?? []).map((blocker) => ({
    id: blocker.id,
    severity: blocker.severity,
    ...resolveView(blocker.id, blocker.metadata as Record<string, unknown>),
  }))
);

const showAction = (action?: BlockerAction): boolean => {
  if (!action) return false;
  if (action.hideOnRoute && route.name === action.hideOnRoute) return false;
  return true;
};
</script>
